// EsConfigContext.tsx
import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	ReactNode,
} from 'react'
import {
	CodeSystem,
	EsConfigMap,
	ESProperty,
	ElasticsearchConfig,
} from './types'
import defaultConfig from './defaultConfig'

type Updates = Partial<
	Pick<ESProperty, 'weight' | 'searchEnabled' | 'returnInResults'>
>

export type NestedPropertyUpdate = {
	parentName: string
	nestedName: string
	updates: Updates
}

interface EsConfigContextValue {
	configMap: EsConfigMap
	updateProperty: (key: CodeSystem, propName: string, updates: Updates) => void
	updateNestedProperty: (
		key: CodeSystem,
		parentName: string,
		nestedName: string,
		updates: Updates
	) => void
	batchUpdateNestedProperties: (
		key: CodeSystem,
		updates: NestedPropertyUpdate[]
	) => void
	updateMinimumNestedFieldScore: (key: CodeSystem, value: number) => void
	updateMaximumNestedFieldValues: (key: CodeSystem, value: number) => void
	updateMinimumScore: (key: CodeSystem, value: number) => void
	updateDisplayScore: (key: CodeSystem, value: boolean) => void
	revertConfig: (key: CodeSystem) => void
	clearCodeSystemStorage: (key: CodeSystem) => void
}

const STORAGE_KEY_PREFIX = 'esConfig_'

const EsConfigContext = createContext<EsConfigContextValue | undefined>(
	undefined
)

// Helper function to get storage key for a specific code system
const getStorageKey = (codeSystem: CodeSystem): string => {
	return `${STORAGE_KEY_PREFIX}${codeSystem}`
}

// Helper function to load config for a specific code system from localStorage
const loadCodeSystemConfig = (codeSystem: CodeSystem) => {
	const stored = localStorage.getItem(getStorageKey(codeSystem))
	if (stored) {
		try {
			const parsedConfig = JSON.parse(stored)
			const defaultConfigForSystem = defaultConfig[codeSystem]

			// Check if the cached config version matches the default config version
			if (
				defaultConfigForSystem &&
				parsedConfig.configVersion !== defaultConfigForSystem.configVersion
			) {
				console.log(
					`Config version mismatch for ${codeSystem}. ` +
						`Cached: ${parsedConfig.configVersion}, Default: ${defaultConfigForSystem.configVersion}. ` +
						`Loading default config.`
				)
				return defaultConfigForSystem
			}

			// Check if the cached indexName has the correct environment
			if (
				defaultConfigForSystem &&
				parsedConfig.indexName !== defaultConfigForSystem.indexName
			) {
				console.log(
					`Index name mismatch for ${codeSystem}. ` +
						`Cached: ${parsedConfig.indexName}, Default: ${defaultConfigForSystem.indexName}. ` +
						`Loading default config.`
				)
				return defaultConfigForSystem
			}

			return parsedConfig
		} catch (error) {
			console.warn(`Failed to parse stored config for ${codeSystem}:`, error)
		}
	}
	return defaultConfig[codeSystem]
}

// Helper function to save config for a specific code system to localStorage
const saveCodeSystemConfig = (
	codeSystem: CodeSystem,
	config: ElasticsearchConfig
) => {
	try {
		localStorage.setItem(getStorageKey(codeSystem), JSON.stringify(config))
	} catch (error) {
		console.warn(`Failed to save config for ${codeSystem}:`, error)
	}
}

// Helper function to migrate from old storage format to new format
const migrateOldStorageFormat = () => {
	const oldStorageKey = 'esConfig'
	const oldConfig = localStorage.getItem(oldStorageKey)

	if (oldConfig) {
		try {
			const parsedOldConfig = JSON.parse(oldConfig)
			// Save each code system config individually
			Object.entries(parsedOldConfig).forEach(([codeSystem, config]) => {
				if (config) {
					saveCodeSystemConfig(
						codeSystem as CodeSystem,
						config as ElasticsearchConfig
					)
				}
			})
			// Remove the old storage key
			localStorage.removeItem(oldStorageKey)
			console.log('Migrated ES config from old storage format to new format')
		} catch (error) {
			console.warn('Failed to migrate old ES config storage format:', error)
		}
	}
}

export const EsConfigProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [configMap, setConfigMap] = useState<EsConfigMap>(() => {
		// First, migrate any old storage format
		migrateOldStorageFormat()

		// Load each code system's config individually from localStorage
		const loadedConfig: EsConfigMap = {}
		Object.values(CodeSystem).forEach((codeSystem) => {
			const config = loadCodeSystemConfig(codeSystem)
			if (config) {
				loadedConfig[codeSystem] = config
			}
		})

		// Merge with default config to ensure all code systems are present
		return { ...defaultConfig, ...loadedConfig }
	})

	// Effect to save individual code system configs when they change
	useEffect(() => {
		Object.entries(configMap).forEach(([codeSystem, config]) => {
			if (config) {
				saveCodeSystemConfig(codeSystem as CodeSystem, config)
			}
		})
	}, [configMap])

	const updateMinimumScore = useCallback((key: CodeSystem, value: number) => {
		setConfigMap((prev) => {
			const cfg = prev[key]
			if (!cfg) {
				throw new Error(`No config found for key "${key}"`)
			}
			return {
				...prev,
				[key]: {
					...cfg,
					minimumScore: value,
				},
			}
		})
	}, [])

	const updateMinimumNestedFieldScore = useCallback(
		(key: CodeSystem, value: number) => {
			setConfigMap((prev) => {
				const cfg = prev[key]
				if (!cfg) {
					throw new Error(`No config found for key "${key}"`)
				}
				return {
					...prev,
					[key]: {
						...cfg,
						minimumNestedFieldScore: value,
					},
				}
			})
		},
		[]
	)

	const updateMaximumNestedFieldValues = useCallback(
		(key: CodeSystem, value: number) => {
			setConfigMap((prev) => {
				const cfg = prev[key]
				if (!cfg) {
					throw new Error(`No config found for key "${key}"`)
				}
				return {
					...prev,
					[key]: {
						...cfg,
						maximumNestedFieldValues: value,
					},
				}
			})
		},
		[]
	)

	const updateDisplayScore = useCallback((key: CodeSystem, value: boolean) => {
		setConfigMap((prev) => {
			const cfg = prev[key]
			if (!cfg) {
				throw new Error(`No config found for key "${key}"`)
			}
			return {
				...prev,
				[key]: {
					...cfg,
					displayScore: value,
				},
			}
		})
	}, [])

	const updateProperty = useCallback(
		(key: CodeSystem, propName: string, updates: Updates) => {
			console.log(`Updating property ${propName} for ${key}:`, updates)
			setConfigMap((prev) => {
				const cfg = prev[key]
				if (!cfg) {
					throw new Error(`No config found for key "${key}"`)
				}
				return {
					...prev,
					[key]: {
						...cfg,
						properties: cfg.properties.map((p) =>
							p.propertyName === propName ? { ...p, ...updates } : p
						),
					},
				}
			})
		},
		[]
	)

	const updateNestedProperty = useCallback(
		(
			key: CodeSystem,
			parentName: string,
			nestedName: string,
			updates: Updates
		) => {
			setConfigMap((prev) => {
				console.log(
					`Updating nested property ${parentName}.${nestedName} for ${key}:`,
					updates
				)
				const cfg = prev[key]
				if (!cfg) {
					throw new Error(`No config found for key "${key}"`)
				}
				return {
					...prev,
					[key]: {
						...cfg,
						nestedProperties: cfg.nestedProperties?.map((np) =>
							np.propertyName !== parentName
								? np
								: {
										...np,
										nestedFields: np.nestedFields.map((f) =>
											f.propertyName === nestedName ? { ...f, ...updates } : f
										),
								  }
						),
					},
				}
			})
		},
		[]
	)

	const batchUpdateNestedProperties = useCallback(
		(key: CodeSystem, updates: NestedPropertyUpdate[]) => {
			setConfigMap((prev) => {
				const cfg = prev[key]
				if (!cfg) {
					throw new Error(`No config found for key "${key}"`)
				}

				// Apply all updates in a single state change
				return {
					...prev,
					[key]: {
						...cfg,
						nestedProperties: cfg.nestedProperties?.map((np) => {
							// Find all updates for this parent
							const parentUpdates = updates.filter(
								(u) => u.parentName === np.propertyName
							)
							if (parentUpdates.length === 0) return np

							return {
								...np,
								nestedFields: np.nestedFields.map((f) => {
									// Find update for this specific nested field
									const fieldUpdate = parentUpdates.find(
										(u) => u.nestedName === f.propertyName
									)
									return fieldUpdate ? { ...f, ...fieldUpdate.updates } : f
								}),
							}
						}),
					},
				}
			})
		},
		[]
	)

	const revertConfig = useCallback((key: CodeSystem) => {
		setConfigMap((prev) => ({
			...prev,
			[key]: defaultConfig[key],
		}))
	}, [])

	const clearCodeSystemStorage = useCallback((key: CodeSystem) => {
		try {
			localStorage.removeItem(getStorageKey(key))
			// Also revert to default config in state
			setConfigMap((prev) => ({
				...prev,
				[key]: defaultConfig[key],
			}))
			console.log(`Cleared storage for code system: ${key}`)
		} catch (error) {
			console.warn(`Failed to clear storage for ${key}:`, error)
		}
	}, [])

	return (
		<EsConfigContext.Provider
			value={{
				configMap,
				updateProperty,
				updateNestedProperty,
				batchUpdateNestedProperties,
				revertConfig,
				clearCodeSystemStorage,
				updateMinimumNestedFieldScore,
				updateMaximumNestedFieldValues,
				updateDisplayScore,
				updateMinimumScore,
			}}
		>
			{children}
		</EsConfigContext.Provider>
	)
}

export function useEsConfig(key: CodeSystem) {
	const ctx = useContext(EsConfigContext)
	if (!ctx) throw new Error('useEsConfig must be inside EsConfigProvider')
	const {
		configMap,
		updateProperty,
		updateNestedProperty,
		batchUpdateNestedProperties,
		revertConfig,
		clearCodeSystemStorage,
		updateMinimumNestedFieldScore,
		updateMaximumNestedFieldValues,
		updateDisplayScore,
		updateMinimumScore,
	} = ctx
	const config = configMap[key]
	if (!config) throw new Error(`No config for key "${key}"`)
	return {
		config,
		updateProperty: (prop: string, u: Updates) => updateProperty(key, prop, u),
		updateNestedProperty: (parent: string, prop: string, u: Updates) =>
			updateNestedProperty(key, parent, prop, u),
		batchUpdateNestedProperties: (updates: NestedPropertyUpdate[]) =>
			batchUpdateNestedProperties(key, updates),
		revert: () => revertConfig(key),
		clearStorage: () => clearCodeSystemStorage(key),
		updateMinimumNestedFieldScore: (value: number) =>
			updateMinimumNestedFieldScore(key, value),
		updateMaximumNestedFieldValues: (value: number) =>
			updateMaximumNestedFieldValues(key, value),
		updateDisplayScore: (value: boolean) => updateDisplayScore(key, value),
		updateMinimumScore: (value: number) => updateMinimumScore(key, value),
	}
}
